import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useFocoPreso from '../hooks/useFocoPreso';
import { useAcessibilidade } from '../contexts/AcessibilidadeContext';
import { NAMESPACE } from '../acessibilidadePainel/i18n';
import { ehPadrao } from '../acessibilidadePainel/preferencias';
import '../styles/AcessibilidadePreferencias.css';

// Painel "Acessibilidade" (item do menu da engrenagem, components/Navbar.js).
// Cada controle grava na hora (sem botão Salvar): a pessoa vê o efeito
// enquanto escolhe. Ver contexts/AcessibilidadeContext.js.
const OPCOES_FONTE = [
  { valor: 'normal', rotulo: 'fontNormal' },
  { valor: 'grande', rotulo: 'fontLarge' },
  { valor: 'muito_grande', rotulo: 'fontExtraLarge' }
];

const Interruptor = ({ id, rotulo, dica, marcado, aoMudar }) => (
  <div className="a11y-opcao">
    <input
      type="checkbox"
      role="switch"
      id={id}
      className="a11y-interruptor"
      checked={marcado}
      onChange={(e) => aoMudar(e.target.checked)}
      aria-describedby={`${id}-dica`}
    />
    <label htmlFor={id} className="a11y-opcao-texto">
      <span className="a11y-opcao-rotulo">{rotulo}</span>
      <span id={`${id}-dica`} className="a11y-opcao-dica">{dica}</span>
    </label>
  </div>
);

const PainelAcessibilidade = ({ aberto, aoFechar }) => {
  const { t } = useTranslation(NAMESPACE);
  const { preferencias, alterar, restaurar, idiomaPt } = useAcessibilidade();
  const caixaRef = useFocoPreso(aberto, { aoFechar });

  if (!aberto) return null;

  return createPortal(
    <div className="a11y-painel-fundo" onMouseDown={aoFechar}>
      <div
        ref={caixaRef}
        className="a11y-painel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="a11y-painel-titulo"
        aria-describedby="a11y-painel-intro"
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="a11y-painel-topo">
          <h2 id="a11y-painel-titulo">{t('title')}</h2>
          <button type="button" className="a11y-painel-fechar" onClick={aoFechar} aria-label={t('close')} title={t('close')}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
        <p id="a11y-painel-intro" className="a11y-painel-intro">{t('intro')}</p>

        <fieldset className="a11y-grupo" aria-describedby="a11y-fonte-dica">
          <legend className="a11y-opcao-rotulo">{t('fontSize')}</legend>
          <div className="a11y-segmentos">
            {OPCOES_FONTE.map(({ valor, rotulo }) => (
              <label key={valor} className={`a11y-segmento${preferencias.fonte === valor ? ' ativo' : ''}`}>
                <input
                  type="radio"
                  name="a11y-fonte"
                  value={valor}
                  checked={preferencias.fonte === valor}
                  onChange={() => alterar({ fonte: valor })}
                />
                <span>{t(rotulo)}</span>
              </label>
            ))}
          </div>
          <span id="a11y-fonte-dica" className="a11y-opcao-dica">{t('fontHint')}</span>
        </fieldset>

        <Interruptor
          id="a11y-alto-contraste"
          rotulo={t('highContrast')}
          dica={t('highContrastHint')}
          marcado={preferencias.altoContraste}
          aoMudar={(valor) => alterar({ altoContraste: valor })}
        />
        <Interruptor
          id="a11y-sem-animacao"
          rotulo={t('pauseAnimations')}
          dica={t('pauseAnimationsHint')}
          marcado={preferencias.semAnimacao}
          aoMudar={(valor) => alterar({ semAnimacao: valor })}
        />
        <Interruptor
          id="a11y-espacamento"
          rotulo={t('textSpacing')}
          dica={t('textSpacingHint')}
          marcado={preferencias.espacamento}
          aoMudar={(valor) => alterar({ espacamento: valor })}
        />
        {idiomaPt ? (
          <Interruptor
            id="a11y-vlibras"
            rotulo={t('vlibras')}
            dica={t('vlibrasHint')}
            marcado={preferencias.vlibras}
            aoMudar={(valor) => alterar({ vlibras: valor })}
          />
        ) : (
          <p className="a11y-nota" data-testid="a11y-vlibras-nota">{t('vlibrasOnlyPt')}</p>
        )}

        <div className="a11y-painel-acoes">
          <button type="button" className="a11y-botao contorno" onClick={restaurar} disabled={ehPadrao(preferencias)}>
            {t('restore')}
          </button>
          <button type="button" className="a11y-botao" onClick={aoFechar}>
            {t('close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PainelAcessibilidade;
